import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import ChirpPropShapes from 'javascripts/shared/chirp_prop_shapes';

import { AuthorByline } from 'javascripts/components/book_card/author_byline';
import { Badge } from 'javascripts/components/badge';
import { CoverImage } from 'javascripts/components/cover_image';
import { StarRating } from 'javascripts/components/star_rating';
import { BookDetailsModal } from 'javascripts/components/checkout/book_details_modal';

import { bubEvents } from 'javascripts/shared/events/bub_events';
import { useMediaQuery } from 'react-responsive';

import { ReactComponent as TrophyIcon } from 'images/trophy';

import styles from 'stylesheets/components/book_card.module';

const MIN_RATINGS_COUNT = 10;

export function BookCard(props) {
  const isOwned = !_.isNil(props.audiobook.currentUserUserAudiobook);

  // [CG 12/11/2020] breakpoint optimized for book cards shown in 4 columns across full site width
  const useSmallStarRating = useMediaQuery({ maxWidth: 1000 });

  const [showBookDetailsModal, setShowBookDetailsModal] = useState(false);

  function trackCoverClick() {
    if (props.trackingCategory) {
      bubEvents.track(props.trackingCategory, 'Clicked audiobook cover', {
        ...props.trackingProperties,
        label: 'Audiobook id',
        value: props.audiobook.id,
      });
    }

    if (props.useBookDetailsModal) {
      setShowBookDetailsModal(true);
    }
  }

  function trackTitleClick() {
    if (props.trackingCategory) {
      bubEvents.track(props.trackingCategory, 'Clicked audiobook title', {
        ...props.trackingProperties,
        label: 'Audiobook id',
        value: props.audiobook.id,
      });
    }
  }

  function trackRatingsClick() {
    if (props.trackingCategory) {
      bubEvents.track(props.trackingCategory, 'Clicked ratings link', {
        ...props.trackingProperties,
        label: 'Audiobook id',
        value: props.audiobook.id,
      });
    }
  }

  function shouldShowPricing() {
    return !!(props.showPricing && props.audiobook.currentProduct);
  }

  function renderTitleLink() {
    const titleLinkProps = {};
    if (props.openNewTab) {
      titleLinkProps.target = '_blank';
      titleLinkProps.rel = 'noopener';
    }

    return (
      <a {...titleLinkProps} href={props.audiobook.url} onClick={trackTitleClick}>
        {props.audiobook.displayTitle}
      </a>
    );
  }

  function renderListingAndDiscountPricing() {
    const discountPrice = (
      <div
        className={styles.discountPrice}
        aria-label={`Discount price: ${props.audiobook.currentProduct.discountPrice}`}
      >
        {props.audiobook.currentProduct.discountPrice}
      </div>
    );

    if (props.audiobook.currentProduct.isFreeListing) {
      return (
        <React.Fragment>
          <div className={styles.freeListing}>Our Price:</div>
          {discountPrice}
        </React.Fragment>
      );
    }

    if (!props.audiobook.currentProduct.showListingPrice) {
      return discountPrice;
    }

    return (
      <React.Fragment>
        <div className={styles.listingPrice} aria-hidden='true'>
          {props.audiobook.currentProduct.listingPrice}
        </div>
        {discountPrice}
      </React.Fragment>
    );
  }

  function renderStarRating() {
    if (!props.showStarRating || (props.audiobook.ratingsCount || 0) < MIN_RATINGS_COUNT) {
      return null;
    }

    const starSize = useSmallStarRating ? 'small' : 'medium';

    const url = props.audiobook.url || `/audiobooks/${props.audiobook.id}`;
    return (
      <a href={`${url}?reviews=t`} className={styles.starRatingWrapper} data-testid='star-rating-link' onClick={trackRatingsClick}>
        <StarRating
          rating={props.audiobook.averageRating}
          ratingsCount={props.audiobook.ratingsCount}
          showRatingsCount
          showRatingsCountFullText={false}
          singleLineDisplay
          size={props.useSmallText ? 'xsmall' : starSize}
          textSize={props.useSmallText ? 'xsmall' : 'small'}
        />
      </a>
    );
  }

  function renderPricing() {
    if (props.audiobook.futureSaleDate) {
      return (
        <div className={styles.pricing}>
          <div className={styles.futureSaleDate}>
            <span className={styles.releaseDateLabel}>Release:</span>
            <span>{props.audiobook.futureSaleDate}</span>
          </div>
        </div>
      );
    }

    if (!shouldShowPricing()) {
      return null;
    }

    return <div className={styles.pricing}>{renderListingAndDiscountPricing()}</div>;
  }

  function renderOwnedBadge() {
    return (
      <div className={styles.ownedBadgeWrapper}>
        <Badge
          text='You own this book'
          size={props.useSmallText ? 'small' : 'medium'}
          badgeBackgroundColor='white'
          elevated
        />
      </div>
    );
  }

  function renderTimeLeft() {
    if (!shouldShowPricing()) {
      return null;
    }

    if (props.audiobook.currentProduct.timeLeft) {
      return <div className={styles.secondaryText}>{`${props.audiobook.currentProduct.timeLeft}`}</div>;
    }
    return null;
  }

  function renderAwardNames() {
    if (!props.showAwardNames || _.isEmpty(props.audiobook.awardWinnersTags)) {
      return null;
    }

    const awardNamesList = props.audiobook.awardWinnersTags.map(tag => tag.displayName);
    const awardNames = awardNamesList.join(', ');

    return (
      <div className={styles.awardNamesWrapper}>
        <TrophyIcon className={styles.trophyIcon} />
        <span className={styles.awardNames}>{awardNames}</span>
      </div>
    );
  }

  function handleAddToCartOnClickHandler() {
    props.addToCartOnClickHandler();
    setShowBookDetailsModal(false);
  }

  function renderBookDetailsModal() {
    if (!props.useBookDetailsModal) {
      return null;
    }

    return (
      <BookDetailsModal
        audiobook={props.audiobook}
        isOpen={showBookDetailsModal}
        showBuyNowAndAddToCartCtas={!props.hideBookDetailsModalButtons}
        onRequestClose={() => setShowBookDetailsModal(false)}
        addToCartOnClickHandler={() => handleAddToCartOnClickHandler()}
      />
    );
  }

  return (
    <div
      className={classNames(styles.bookCard, {
        [styles.small]: props.useSmallText,
      })}
      data-testid='book-card'
    >
      {isOwned && renderOwnedBadge()}
      <CoverImage
        bookAuthor={props.audiobook.displayAuthors}
        bookId={props.audiobook.id}
        bookTitle={props.audiobook.displayTitle}
        coverUrl={props.audiobook.coverUrl}
        currentProduct={props.audiobook.currentProduct}
        inCart={!_.isNil(props.audiobook.currentUserShoppingCartItem)}
        inWishlist={!_.isNil(props.audiobook.currentUserWishlistItem)}
        linkUrl={props.useBookDetailsModal ? null : props.audiobook.url}
        onClick={trackCoverClick}
        openNewTab={props.openNewTab}
        bannerType={shouldShowPricing() ? props.audiobook.currentProduct?.bannerType : null }
        showShoppingButtons={props.showCoverButtons && !isOwned}
        size='medium'
        trackingCategory={props.trackingCategory}
        buttonLabel={props.useBookDetailsModal ? `View more details about ${props.audiobook.displayTitle}` : null}
      />
      <h3 className={styles.title}>{renderTitleLink()}</h3>
      <span className={styles.byline}>
        <AuthorByline
          audiobook={props.audiobook}
          trackingCategory={props.trackingCategory}
          trackingProperties={props.trackingProperties}
          useSmallText={props.useSmallText}
        />
      </span>
      {renderStarRating()}
      {renderPricing()}
      {renderTimeLeft()}
      {renderAwardNames()}
      {renderBookDetailsModal()}
    </div>
  );
}

BookCard.propTypes = {
  audiobook: ChirpPropShapes.audiobook.isRequired,
  coverSize: PropTypes.oneOf(['medium', 'large']),
  trackingCategory: PropTypes.string,
  openNewTab: PropTypes.bool,
  showCoverButtons: PropTypes.bool,
  showPricing: PropTypes.bool,
  showStarRating: PropTypes.bool,
  useSmallText: PropTypes.bool,
  trackingProperties: PropTypes.object,
  showAwardNames: PropTypes.bool,
  useBookDetailsModal: PropTypes.bool,
  hideBookDetailsModalButtons: PropTypes.bool,
  addToCartOnClickHandler: PropTypes.func.isRequired,
};

BookCard.defaultProps = {
  coverSize: 'medium',
  openNewTab: false,
  showCoverButtons: false,
  showPricing: true,
  showStarRating: false,
  useSmallText: false,
  trackingProperties: {},
  showAwardNames: false,
  useBookDetailsModal: false,
  hideBookDetailsModalButtons: false,
  addToCartOnClickHandler: _.noop,
};
